import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

/**
 * Auto-logout hook - logs out user after specified inactivity period
 * @param {number} timeoutMinutes - Minutes of inactivity before logout (default: 60)
 */
export function useAutoLogout(timeoutMinutes = 60) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  const TIMEOUT_MS = timeoutMinutes * 60 * 1000; // Convert to milliseconds

  const handleLogout = useCallback(() => {
    if (user) {
      console.log('Auto-logout: Session expired due to inactivity');
      logout();
      navigate('/login', { replace: true });
    }
  }, [user, logout, navigate]);

  const resetTimer = useCallback(() => {
    lastActivityRef.current = Date.now();
    
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    
    // Set new timeout
    if (user) {
      timeoutRef.current = setTimeout(handleLogout, TIMEOUT_MS);
    }
  }, [user, handleLogout, TIMEOUT_MS]);

  useEffect(() => {
    if (!user) {
      // Clear timeout if user logs out
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    // Activity events to track
    const events = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'focus'
    ];

    // Throttle activity tracking to avoid excessive calls
    let throttleTimer = null;
    const throttledResetTimer = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        resetTimer();
        throttleTimer = null;
      }, 1000); // Only reset timer once per second max
    };

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, throttledResetTimer, { passive: true });
    });

    // Also track visibility changes (user switching tabs)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if we should have logged out while tab was hidden
        const elapsed = Date.now() - lastActivityRef.current;
        if (elapsed >= TIMEOUT_MS) {
          handleLogout();
        } else {
          resetTimer();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initial timer setup
    resetTimer();

    // Cleanup
    return () => {
      events.forEach(event => {
        document.removeEventListener(event, throttledResetTimer);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (throttleTimer) {
        clearTimeout(throttleTimer);
      }
    };
  }, [user, resetTimer, handleLogout, TIMEOUT_MS]);

  return {
    resetTimer,
    lastActivity: lastActivityRef.current
  };
}
